import type { DateFormat, TimeFormat } from '@uchat-team-management/shared';
import dayjs, { type Dayjs } from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';
import { createSignal } from 'solid-js';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(relativeTimePlugin);

export interface DateTimePrefs {
  dateFormat: DateFormat | null;
  timeFormat: TimeFormat | null;
  timezone: string | null;
}

// Module-level signal: reads inside JSX are tracked, so every call site
// re-renders when AuthProvider syncs new preferences. null = default.
const [prefs, setPrefs] = createSignal<DateTimePrefs>({
  dateFormat: null,
  timeFormat: null,
  timezone: null,
});
export const setDateTimePrefs = setPrefs;

// Date presets are already dayjs tokens; null falls back to the defaults below.
export const DEFAULT_DATE_TOKEN = 'DD/MM/YYYY';
export const DEFAULT_TIME_TOKEN = 'HH:mm:ss';
const TIME_TOKENS: Record<TimeFormat, string> = { '24h': 'HH:mm', '12h': 'h:mm A' };

const dateToken = () => prefs().dateFormat ?? DEFAULT_DATE_TOKEN;
const timeToken = () => {
  const preset = prefs().timeFormat;
  return preset ? TIME_TOKENS[preset] : DEFAULT_TIME_TOKEN;
};

/** The browser's own timezone. */
export const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Optional per-call zone used only while the user's timezone preference is Auto. */
export interface FormatOptions {
  fallbackTimeZone?: string;
}

/** The IANA zone the format helpers will actually use, for "times in …" labels. */
export const effectiveTimeZone = (opts: FormatOptions = {}): string =>
  prefs().timezone ?? opts.fallbackTimeZone ?? deviceTimeZone;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
export const isDateOnly = (value: string): boolean => DATE_ONLY.test(value);

/** Parse an ISO instant into `tz`; null for empty/invalid input. Unknown zones degrade to the device zone. */
const toDay = (iso: string, tz: string): Dayjs | null => {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  try {
    return dayjs(iso).tz(tz);
  } catch {
    return dayjs(iso);
  }
};

/** Format an instant with an explicit dayjs token; '' for empty/invalid input. */
export const formatWith = (iso: string, token: string, tz = effectiveTimeZone()): string =>
  toDay(iso, tz)?.format(token) ?? '';

/** Date part of an instant per the user's preferences; '' for empty/invalid input. */
export const formatDate = (iso: string, opts: FormatOptions = {}): string =>
  formatWith(iso, dateToken(), effectiveTimeZone(opts));

/** A calendar date (YYYY-MM-DD) in the user's date format, never shifted by timezone. */
export const formatCalendarDate = (ymd: string, token: string = dateToken()): string => {
  if (!isDateOnly(ymd)) return '';
  const d = dayjs(ymd);
  return d.isValid() ? d.format(token) : '';
};

/** A wall-clock date-time with no zone (YYYY-MM-DDTHH:mm), formatted without shifting. */
export const formatWallClock = (value: string): string => {
  const d = dayjs(value);
  return value && d.isValid() ? d.format(`${dateToken()}, ${timeToken()}`) : '';
};

/** Date + time of an instant per the user's preferences; '' for empty/invalid input. */
export const formatDateTime = (iso: string, opts: FormatOptions = {}): string =>
  formatWith(iso, `${dateToken()}, ${timeToken()}`, effectiveTimeZone(opts));

/** Time of day only (charts, timelines), same preference rules as formatDateTime. */
export const formatTime = (iso: string, opts: FormatOptions = {}): string =>
  formatWith(iso, timeToken(), effectiveTimeZone(opts));

/** "UTC+7", "UTC-3:30", "UTC" for a timezone at a given instant. */
export const utcOffsetLabel = (tz: string, iso: string): string => {
  const minutes = toDay(iso, tz)?.utcOffset() ?? 0;
  if (minutes === 0) return 'UTC';
  const abs = Math.abs(minutes);
  const m = abs % 60;
  return `UTC${minutes > 0 ? '+' : '-'}${Math.floor(abs / 60)}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
};

export { dayjs };
